import Alert from '../../../common/components/Alert';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { getCookie } from '../../../common/utils/cookies';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { AuthPageStyles } from '../../styles';
import { IELink } from '../../../common/components/IELink';
import { Link as RouterLink } from 'react-router-dom';
import { MfsLoginFields } from '../../interfaces/AuthInputFields';
import { MfsLoginMode } from '../../../constants/mfsLoginModes';
import { MfsLoginModeType } from './MfsLoginPage';
import { PasswordStrengthMessages } from '../PasswordStrengthMessages';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter';
import { routeMappings } from '../../../constants';
import { SessionStore } from '../../../constants/sessionStore';
import { useTranslation } from 'react-i18next';
import { createStyles, makeStyles } from '@material-ui/core';
import { useAppSelector } from '../../../redux-store/hook';

interface MfsLoginFormProps {
  loginMode: MfsLoginModeType;
  formFields: MfsLoginFields;
  errorMessage: string;
  loadingOnLogin: boolean;
  onLoginClick(event: React.SyntheticEvent): Promise<void>;
  onInputChange(name: keyof MfsLoginFields): (event: React.ChangeEvent<{ value: string }>) => void;
}
const useStyles = makeStyles(() => ({
  passwordContainer: createStyles((value: { password: string }) => {
    return {
      '&:after': {
        content: `'${value.password?.replace(/[\s\S]/g, () => '●') ?? ''}'`,
      },
    };
  }),
  passwordContainerConfirm: createStyles((value: { passwordCnf: string }) => {
    return {
      '&:after': {
        content: `'${value.passwordCnf?.replace(/[\s\S]/g, () => '●') ?? ''}'`,
      },
    };
  }),
}));

/** SignUp Component for MFS */
export const MfsLoginForm = ({
  formFields,
  loginMode,
  errorMessage,
  loadingOnLogin,
  onLoginClick,
  onInputChange,
}: MfsLoginFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const classes = AuthPageStyles({ isICPEnabled: isICPEnabled });
  const dynamicClass = useStyles({ password: formFields.password ?? '', passwordCnf: formFields.confirmPassword });
  const logoutDueToInactivity = sessionStorage.getItem(SessionStore.logoutDueToInactivity);
  const isSSOEnabled = !!getCookie('mfsSSOEnabled') || false;

  const getKeyByLoginMode = (): { pageTitle: string; buttonText: string } => {
    let pageTitle = '';
    let buttonText = '';
    switch (loginMode) {
      case MfsLoginMode.SIGNIN:
        pageTitle = 'signIn';
        buttonText = 'signIn';
        break;
      case MfsLoginMode.FORGOT_RESET:
        pageTitle = 'resetPassword';
        buttonText = 'changePasswordAndSignIn';
        break;
      case MfsLoginMode.SIGNUP:
      default:
        pageTitle = 'mfsLoginPageTitle';
        buttonText = 'confirmPasswordAndSignIn';
        break;
    }
    return { pageTitle, buttonText };
  };

  if (isSSOEnabled)
    return (
      <>
        <Box mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Box mb={3} className={classes.padding12}>
                  <Typography variant="h2">{t('mfsSSoMessageHeading')}</Typography>
                  <Typography>
                    <strong>{t('mfsSSoMessageLine1')}</strong>
                  </Typography>
                  <Typography>{t('mfsSSoMessageLine2')}</Typography>
                  <Typography>{t('mfsSSoMessageLine3')}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </>
    );

  return (
    <form onSubmit={onLoginClick} aria-label={t(getKeyByLoginMode().pageTitle)}>
      <Box mb={3}>
        <Typography variant="h2">{t(getKeyByLoginMode().pageTitle)}</Typography>
      </Box>

      <Box mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {loginMode === MfsLoginMode.SIGNIN ? (
                <Grid item xs={12} sm={6}>
                  {/** username */}
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="username">
                        <b>{t('username')}</b>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="username"
                        variant="outlined"
                        required
                        fullWidth
                        name="username"
                        autoComplete="off"
                        size="small"
                        type="text"
                        placeholder={t('usernamePlaceholder')}
                        value={formFields.username}
                        error={formFields.usernameHelperText !== ''}
                        helperText={formFields.usernameHelperText !== '' && t(formFields.usernameHelperText)}
                        onChange={onInputChange('username')}
                        onBlur={onInputChange('username')}
                        inputProps={{
                          'aria-label': 'username',
                          maxLength: '100',
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}

              <Grid item xs={12} sm={6}>
                {/** Password */}
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <InputLabel htmlFor="password">
                      <b>{t('password')}</b>
                    </InputLabel>
                  </Grid>
                  <Grid item xs={12} className={`${dynamicClass.passwordContainer} ${classes.passwordContainer} `}>
                    <TextField
                      id="password"
                      type="text"
                      variant="outlined"
                      autoComplete="off"
                      required
                      fullWidth
                      name="password"
                      size="small"
                      value={formFields.password}
                      error={formFields.passwordHelperText !== ''}
                      helperText={formFields.passwordHelperText !== '' && t(formFields.passwordHelperText)}
                      placeholder={t('passwordPlaceholder')}
                      onChange={onInputChange('password')}
                      onBlur={onInputChange('password')}
                      inputProps={{
                        'aria-label': 'password',
                        maxLength: '50',
                      }}
                    />
                  </Grid>
                </Grid>

                {loginMode === MfsLoginMode.SIGNIN ? (
                  <Grid item xs={12}>
                    <Box pt={1}>
                      <Link component={RouterLink} to={`${routeMappings['MFS']}${routeMappings['FORGOT_PASSWORD']}`}>
                        <IELink>{`${t('forgottenPassword')}?`}</IELink>
                      </Link>
                    </Box>
                  </Grid>
                ) : null}
              </Grid>

              {loginMode === MfsLoginMode.SIGNUP || loginMode === MfsLoginMode.FORGOT_RESET ? (
                <>
                  <Grid item xs={12} sm={6}>
                    {/** Confirm Password */}
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <InputLabel htmlFor="confirmPassword">
                          <b>{t('confirmPassword')}</b>
                        </InputLabel>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        className={`${dynamicClass.passwordContainerConfirm} ${classes.passwordContainer} `}>
                        <TextField
                          id="confirmPassword"
                          type="text"
                          variant="outlined"
                          required
                          fullWidth
                          name="confirmPassword"
                          placeholder={t('confirmPassword')}
                          size="small"
                          value={formFields.confirmPassword}
                          error={formFields.confirmPasswordHelperText !== ''}
                          helperText={
                            formFields.confirmPasswordHelperText !== '' && t(formFields.confirmPasswordHelperText)
                          }
                          onChange={onInputChange('confirmPassword')}
                          onBlur={onInputChange('confirmPassword')}
                          autoComplete="off"
                          inputProps={{
                            'aria-label': 'confirmPassword',
                            maxLength: '50',
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/** passowrd notes */}
                  <PasswordStrengthMessages
                    email={formFields.email}
                    password={formFields.password}
                    className={classes.progressMeterAlert}
                  />

                  {/** Strength Indicator */}
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12}>
                        <InputLabel htmlFor="strength">
                          <b>{t('passwordStrength')}</b>
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12}>
                        <PasswordStrengthMeter password={formFields.password} />
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              ) : null}

              {loginMode === MfsLoginMode.SIGNUP ? (
                <>
                  <Grid item xs={12} sm={6}>
                    {/** Language */}
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <InputLabel htmlFor="language">
                          <b>{t('language')}</b>
                        </InputLabel>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="language"
                          select={true}
                          value={
                            getCookie('locale_player')?.toLowerCase() ||
                            (formFields.language && formFields.language.toLowerCase())
                          }
                          disabled={false}
                          fullWidth
                          error={formFields.languageHelperText !== ''}
                          helperText={
                            formFields.languageHelperText !== '' ? t(`${formFields.languageHelperText}`) : undefined
                          }
                          size="small"
                          onChange={onInputChange('language')}
                          variant="outlined">
                          {formFields.languages &&
                            formFields.languages.map((lang) => (
                              <MenuItem key={lang.locale} value={lang.locale && lang.locale.toLowerCase()}>
                                {lang.languageName}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </Grid>

          {/** alert message in case sign up api fails */}
          <Alert show={errorMessage !== ''} state="error">
            {ReactHtmlParser(errorMessage, { decodeEntities: true })}
          </Alert>

          {/** alert message for user logout due to inactivity  */}
          <Alert show={logoutDueToInactivity ? JSON.parse(logoutDueToInactivity) : false} state="error">
            {t('signedOutDueToInactivityMsg')}
          </Alert>

          {/** button for sign in */}
          <Grid item xs={12}>
            <Box width="100%" textAlign="center">
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                disabled={loadingOnLogin}
                aria-hidden={loadingOnLogin}
                aria-disabled="false"
                onClick={onLoginClick}>
                <div>{t(getKeyByLoginMode().buttonText)}</div>
                {loadingOnLogin && <CircularProgress size={25} className={classes.buttonProgress} />}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};
